import React, {Fragment} from 'react';
import { withRouter, Link } from 'react-router-dom';
import GridLayout from 'react-grid-layout';
import {FormattedMessage, injectIntl} from "react-intl";
import {Query} from "react-apollo";
import { find, isEqualWith } from 'lodash';

import {Background, utils} from "@treeosk/common"

import { apolloClient } from '../../config';
import '../../style/PageEdition.scss'
import queries from "../../queries";
import CellToolbox from "../cells/CellToolbox";
import CellEditor from "../cells/editors/CellEditor";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import PageSettings from "./PageSettings";
import CellPreview from "../cells/previews/CellPreview";
import InlinePreviewPanel from "../projects/InlinePreviewPanel";
import confirmDialog from "../dialogs/ConfirmDialog";
import ProjectSettings from "../projects/ProjectSettings";
import NeedAdmin from "../NeedAdmin";
import {identityProvider, isUserPowerUser} from "../../utils";
import { showToast } from '../../toast';
import { t } from '../../i18n';
import LayersPanel from './LayersPanel';
import { undo, redo, getHistoryState, subscribeHistory, recordLayoutChange, recordCellAddition, setHistoryScope } from '../../undoRedo';

class PageEdition extends React.Component {

  state = {
    elements: {},
    view: this.props.match.params.cellId ? 'module' : 'toolbox',
    // Center view: 'editor' (canvas) | 'split' | 'preview'. Default = Canvas.
    paneMode: window.innerWidth >= 1600 ? 'split' : 'editor',
    zoomSetting: 'fit', // 'fit' | 0.5 | 0.75 | 1
    shellWidth: 0,
    toolboxTab: 'library',
    // Structure (pages) panel toggled by the rail's Structure icon
    // Sur tablette, le panneau des pages est fermé au départ (le bouton « Structure » l'ouvre).
    structureOpen: window.innerWidth >= 1500,
    menu: null, // menu contextuel d'un module : { x, y, cellId }
    history: getHistoryState(),
  };

  componentDidMount() {
    setHistoryScope(this.props.match.params.projectId);
    this.unsubscribeHistory = subscribeHistory((history) => this.setState({ history }));
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('focusin', this.onFocusIn);
    window.addEventListener('click', this.closeMenu);
    window.addEventListener('scroll', this.closeMenu, true);
  }

  componentWillUnmount() {
    if (this.unsubscribeHistory) this.unsubscribeHistory();
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('focusin', this.onFocusIn);
    window.removeEventListener('click', this.closeMenu);
    window.removeEventListener('scroll', this.closeMenu, true);
  }

  // Suit la largeur disponible pour le canevas : le zoom « Ajuster » s'adapte à la place restante.
  setShell = (el) => {
    if (this.shellEl === el) return;
    if (this.shellObserver) { this.shellObserver.disconnect(); this.shellObserver = null; }
    this.shellEl = el;
    if (el && typeof ResizeObserver !== 'undefined') {
      this.shellObserver = new ResizeObserver(() => {
        const w = el.clientWidth;
        if (Math.abs(w - this.state.shellWidth) > 2) this.setState({ shellWidth: w });
      });
      this.shellObserver.observe(el);
    }
  };

  closeMenu = () => { if (this.state.menu) this.setState({ menu: null }); };

  refetchPage = () => {
    const { pageId } = this.props.match.params;
    return apolloClient.query({
      query: queries.pages.getWithChildren,
      variables: { id: pageId, screen_type_id: this.props.screenType.id },
      fetchPolicy: 'network-only',
    });
  };

  deleteCell = async (cellId) => {
    try {
      await confirmDialog(<FormattedMessage id={'page.editor.deletecell.dialog.message'} values={{br: <br/>}} />);
    } catch (e) { return; }
    const { projectId, pageId } = this.props.match.params;
    await apolloClient.mutate({
      mutation: queries.cells.deleteCell,
      variables: {input: cellId},
      refetchQueries: () => [{ query: queries.pages.getWithChildren, variables: {id: pageId, screen_type_id: this.props.screenType.id} }]
    });
    if (this.props.match.params.cellId === cellId) this.props.history.push(`/project/${projectId}/page/${pageId}`);
    showToast(t('ui.editor.cell_deleted'), 'info');
  };

  duplicateCell = async (cellId) => {
    try {
      const res = await apolloClient.mutate({ mutation: queries.cells.duplicateCell, variables: { input: cellId } });
      await this.refetchPage();
      recordCellAddition({
        label: t('ui.history.label.duplicate'),
        cellId: res.data.duplicateCell.id,
        create: async () => (await apolloClient.mutate({ mutation: queries.cells.duplicateCell, variables: { input: cellId } })).data.duplicateCell.id,
        remove: (id) => apolloClient.mutate({ mutation: queries.cells.deleteCell, variables: { input: id } }),
      });
      showToast(t('ui.editor.cell_duplicated'));
      const { projectId, pageId } = this.props.match.params;
      this.props.history.push(`/project/${projectId}/page/${pageId}/cell/${res.data.duplicateCell.id}`);
    } catch (e) {
      showToast(t('ui.editor.cell_dup_error'), 'error', 5000);
    }
  };

  runHistory = async (direction) => {
    const { canUndo, canRedo } = this.state.history;
    if (direction === 'undo' && !canUndo) { showToast(t('ui.history.nothing_undo'), 'info', 2500); return; }
    if (direction === 'redo' && !canRedo) { showToast(t('ui.history.nothing_redo'), 'info', 2500); return; }
    const label = (direction === 'undo' ? this.state.history.undoLabel : this.state.history.redoLabel) || t('ui.history.label.change');
    await (direction === 'undo' ? undo : redo)();
    showToast(t(direction === 'undo' ? 'ui.history.undone' : 'ui.history.redone', { label }), 'info', 2500);
    // Si le module affiché n'existe plus (ajout annulé), on revient à la liste des modules.
    const { projectId, pageId, cellId } = this.props.match.params;
    if (cellId) {
      try {
        const { data } = await this.refetchPage();
        if (!data.page.cells.some((c) => c.id === cellId)) this.props.history.push(`/project/${projectId}/page/${pageId}`);
      } catch (e) { /* ignore */ }
    }
  };

  onFocusIn = (e) => { if (e.target && e.target.value !== undefined) e.target.__tskFocusValue = e.target.value; };

  onKeyDown = (e) => {
    const t = e.target;
    const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
    if (document.querySelector('.tsk-dialog-container:not([style*="none"])')) return;
    const mod = e.metaKey || e.ctrlKey;
    const isHistoryKey = mod && (e.key.toLowerCase() === 'z' || e.key.toLowerCase() === 'y');
    // Dans un champ : on laisse le navigateur annuler la frappe en cours, sauf si le champ n'a pas été modifié depuis qu'on y est entré
    // (dans ce cas Cmd+Z concerne la dernière modification enregistrée).
    const untouchedField = t && t.__tskFocusValue !== undefined && t.__tskFocusValue === t.value;
    if (typing && !(isHistoryKey && untouchedField)) return;
    const { cellId } = this.props.match.params;
    const key = e.key.toLowerCase();
    if (mod && (key === 'z' || key === 'y')) {
      e.preventDefault();
      this.runHistory(key === 'y' || e.shiftKey ? 'redo' : 'undo');
    } else if (mod && key === 'd' && cellId) {
      e.preventDefault();
      this.duplicateCell(cellId);
    } else if ((key === 'delete' || key === 'backspace') && cellId && isUserPowerUser(identityProvider.currentUser())) {
      e.preventDefault();
      this.deleteCell(cellId);
    } else if (key === 'escape' && cellId) {
      const { projectId, pageId } = this.props.match.params;
      this.props.history.push(`/project/${projectId}/page/${pageId}`);
    }
  };

  componentDidUpdate(prevProps, prevState, snapshot) {
    const prevCellId = prevProps.match.params.cellId;
    const newCellId = this.props.match.params.cellId;
    if (!!newCellId) {
      if (!prevCellId || prevCellId !== newCellId) {
        this.setState({view: 'module'});
      }
    } else if (this.state.view === 'module') {
      this.setState({view: 'toolbox'});
    }
  }

  render() {
    const { screenType, fontRatio, structurePanel, screenTypeSelect } = this.props;
    const { projectId, pageId, cellId } = this.props.match.params;
    const { view, paneMode, structureOpen } = this.state;

    const isPowerUser = isUserPowerUser(identityProvider.currentUser());

    console.log(`Editing page #${pageId} for screenType #${screenType.id}`);

    // Left icon rail nav. `activeView` lets 'toolbox' stay highlighted while a
    // cell is being edited (view === 'module'), since 'module' has no rail icon.
    const activeView = view === 'module' ? 'toolbox' : view;
    const railIcons = { toolbox: 'layer-group', page: 'file-alt', project: 'desktop' };
    const viewChangeButton = (viewName) => {
      return (
        <button
          type="button"
          onClick={() => this.setState({view: viewName})}
          className={`tsk-editor-rail-btn tsk-button${activeView === viewName ? ' current' : ''}`}
          title={this.props.intl.formatMessage({ id: `page.editor.button.${viewName}` })}
        >
          <FontAwesomeIcon icon={railIcons[viewName] || 'file-alt'} />
          <span className="tsk-editor-rail-label">
            <FormattedMessage id={`page.editor.button.${viewName}`} />
          </span>
        </button>
      )
    };

    // Segmented switch controlling the center zone: canvas / split / live preview.
    const paneModeButton = (mode, icon, labelId) => (
      <button
        type="button"
        onClick={() => this.setState({paneMode: mode})}
        className={`tsk-pane-toggle tsk-button${paneMode === mode ? ' current' : ''}`}
      >
        <FontAwesomeIcon icon={icon} />
        <span className="tsk-pane-toggle-label"><FormattedMessage id={labelId} /></span>
      </button>
    );

    const pageEditorView = (viewName, params) => {
      switch(viewName) {
        case 'toolbox':
          return (
            <NeedAdmin>
              <div className="tsk-segmented tsk-toolbox-tabs" role="tablist">
                <button type="button" role="tab" aria-selected={this.state.toolboxTab === 'library'} className={this.state.toolboxTab === 'library' ? 'is-active' : ''} onClick={() => this.setState({ toolboxTab: 'library' })}>{t('ui.layers.tab_library')}</button>
                <button type="button" role="tab" aria-selected={this.state.toolboxTab === 'layers'} className={this.state.toolboxTab === 'layers' ? 'is-active' : ''} onClick={() => this.setState({ toolboxTab: 'layers' })}>{t('ui.layers.tab_layers', { count: params.page.cells.length })}</button>
              </div>
              {this.state.toolboxTab === 'library' ? (
                <CellToolbox {...params}/>
              ) : (
                <LayersPanel
                  cells={params.page.cells}
                  projectId={this.props.match.params.projectId}
                  pageId={this.props.match.params.pageId}
                  selectedId={this.props.match.params.cellId}
                  onDuplicate={this.duplicateCell}
                  onDelete={this.deleteCell}
                />
              )}
            </NeedAdmin>
          );
        case 'module':
          return <CellEditor {...params}/>;
        case 'page':
          return <PageSettings {...params}/>;
        case 'project':
          return <ProjectSettings {...params}/>;
        default:
          return <span>{viewName}</span>;
      }
    };

    const deleteDialogConfirmationContent = (
        <FormattedMessage id={'page.editor.deletecell.dialog.message'} values={{br: <br/>}} />
    );

    return (
      <Query query={queries.pages.getWithChildren} variables={{ id: pageId, screen_type_id: screenType.id }}>
        {({ loading, error, data }) => {
          if (loading) return null;
          if (error) return `Error!: ${error}`;

          const { page } = data;
          const layout = page.cells.map((cell)=> (
            {
              i: cell.id,
              x: cell.coordinates.x,
              y: cell.coordinates.y,
              w: cell.coordinates.width,
              h: cell.coordinates.height
            }
          ));

          function getCellById(cellId) {
            return find(page.cells, {'id': cellId});
          }

          const nbCols = 4*screenType.x_dimension;
          const minRows = 4*screenType.y_dimension;
          const baseWidth = nbCols * 10;
          const zoomSetting = this.state.zoomSetting;
          const fitZoom = this.state.shellWidth ? Math.min(1.4, Math.max(0.35, (this.state.shellWidth - 28) / baseWidth)) : 1;
          const zoom = zoomSetting === 'fit' ? fitZoom : zoomSetting;
          const dimensionSize = 10 * zoom;
          const width = nbCols * dimensionSize;
          const height = minRows * dimensionSize;

          const borderStyle = {
            position: "absolute",
            width,
            height,
            zIndex: 50,
            pointerEvents: "none"
          };

          const bgStyle = {
            position: "relative",
            width,
            height,
          };

          // Visible snap grid. react-grid-layout already snaps drags to dimensionSize; this
          // just draws it. Minor line = one drag step, major line = one screen unit (the same
          // 4x factor used by nbCols/minRows above), so it stays correct for every screenType.
          // zIndex 0 keeps it above the page background but below the positioned .react-grid-items.
          const majorSize = dimensionSize * 4;
          // Centre lines: nbCols/minRows are always even (both are 4 * dimension), so the exact
          // middle always lands on a snap line. Drawn as single non-repeating gradients on top
          // of the repeating ones, sized 1px and positioned at the centre of each axis.
          const centerLine = (deg, size) =>
            `linear-gradient(${deg}deg, var(--tsk-grid-center) 0 1px, transparent 1px) ` +
            `${deg === 90 ? `${size / 2}px 0` : `0 ${size / 2}px`} / ` +
            `${deg === 90 ? `1px 100%` : `100% 1px`} no-repeat`;
          const gridStyle = {
            position: "absolute",
            width,
            height,
            zIndex: 0,
            pointerEvents: "none",
            background: [
              centerLine(90, width),
              centerLine(180, height),
              `repeating-linear-gradient(90deg, var(--tsk-grid-major) 0 1px, transparent 1px ${majorSize}px)`,
              `repeating-linear-gradient(180deg, var(--tsk-grid-major) 0 1px, transparent 1px ${majorSize}px)`,
              `repeating-linear-gradient(90deg, var(--tsk-grid-minor) 0 1px, transparent 1px ${dimensionSize}px)`,
              `repeating-linear-gradient(180deg, var(--tsk-grid-minor) 0 1px, transparent 1px ${dimensionSize}px)`,
            ].join(', '),
          };

          const fontSize = utils.defaultFontSize(screenType, width, height, fontRatio);

          return(
            <div className='tsk-page-edit-container' style={{"--default_font_size": `${fontSize}px`}}>
              {/* Left icon rail: Structure toggle + section nav (Modules / Page option / Project settings) */}
              <div className="tsk-editor-rail" role="tablist" aria-label="Editor sections">
                <button
                  type="button"
                  onClick={() => this.setState({ structureOpen: !structureOpen })}
                  className={`tsk-editor-rail-btn tsk-button${structureOpen ? ' current' : ''}`}
                  title={this.props.intl.formatMessage({ id: 'project.structure' })}
                  aria-pressed={structureOpen}
                >
                  <FontAwesomeIcon icon={'sitemap'} />
                  <span className="tsk-editor-rail-label">
                    <FormattedMessage id="project.structure" />
                  </span>
                </button>
                <NeedAdmin>
                  {viewChangeButton('toolbox')}
                </NeedAdmin>
                <NeedAdmin>
                  {viewChangeButton('page')}
                  {viewChangeButton('project')}
                </NeedAdmin>
              </div>

              {/* Structure (pages) panel, toggled by the rail's Structure icon */}
              {structureOpen && structurePanel}

              {/* Center zone: canvas + live preview, driven by CANVAS/SPLIT/PREVIEW toggle */}
              <div className={`tsk-editor-center tsk-pane-mode-${paneMode}`}>
                <div className="tsk-editor-center-head">
                  <div className="tsk-editor-center-head-left">
                    <Query query={queries.projects.projectPagesForCopy} variables={{ id: projectId }} fetchPolicy="cache-and-network">
                      {({ data: pagesData }) => (pagesData && pagesData.project && pagesData.project.pages.length > 1) ? (
                        <select
                          className="tsk-page-switch"
                          aria-label={t('ui.page.switch')}
                          value={pageId}
                          onChange={(e) => this.props.history.push(`/project/${projectId}/page/${e.target.value}`)}
                        >
                          {pagesData.project.pages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      ) : null}
                    </Query>
                    {screenTypeSelect}
                  </div>
                  <div className="tsk-history-buttons" role="group" aria-label={t('ui.history.group')}>
                    <button type="button" className="tsk-history-btn" disabled={!this.state.history.canUndo} onClick={() => this.runHistory('undo')} title={t('ui.history.undo_title')} aria-label={t('ui.history.undo')}>
                      <FontAwesomeIcon icon="undo" />
                    </button>
                    <button type="button" className="tsk-history-btn" disabled={!this.state.history.canRedo} onClick={() => this.runHistory('redo')} title={t('ui.history.redo_title')} aria-label={t('ui.history.redo')}>
                      <FontAwesomeIcon icon="redo" />
                    </button>
                  </div>
                  <div className="tsk-zoom" role="group" aria-label={t('ui.zoom.group')}>
                    {[['fit', t('ui.zoom.fit')], [0.5, '50%'], [0.75, '75%'], [1, '100%']].map(([value, label]) => (
                      <button
                        key={String(value)}
                        type="button"
                        className={zoomSetting === value ? 'is-active' : ''}
                        aria-pressed={zoomSetting === value}
                        title={value === 'fit' ? t('ui.zoom.fit_title') : t('ui.zoom.level', { level: label })}
                        onClick={() => this.setState({ zoomSetting: value })}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  <div className="tsk-pane-toggle-group" role="group" aria-label="Center view">
                    {paneModeButton('editor', 'pen', 'page.editor.pane.canvas')}
                    {paneModeButton('split', 'columns', 'page.editor.pane.split')}
                    {paneModeButton('preview', 'eye', 'page.editor.pane.preview')}
                  </div>
                </div>
                <div className="tsk-editor-center-body">
                  <div className="tsk-page-canvas-shell" ref={this.setShell}>
              <Background className="tsk-page-grid" value={page} style={bgStyle}>
                <Fragment>
                  <div className="tsk-page-gridlines" style={gridStyle} aria-hidden="true" />
                  <div className="tsk-page-grid-frame" style={borderStyle} aria-hidden="true" />
                  <GridLayout
                              layout={layout}
                              margin={[0,0]}
                              cols={nbCols}
                              rowHeight={dimensionSize}
                              width={nbCols*dimensionSize}
                              isDraggable={isPowerUser}
                              isResizable={isPowerUser}
                              onDragStart={() => { this.userLayoutAction = true; }}
                              onResizeStart={() => { this.userLayoutAction = true; }}
                              onDragStop={() => { setTimeout(() => { this.userLayoutAction = false; }, 800); }}
                              onResizeStop={() => { setTimeout(() => { this.userLayoutAction = false; }, 800); }}
                              onLayoutChange={async (newLayout) => {
                                if(screenType.id !== this.props.screenType.id) {
                                  // We are just changing screen type, nothing to save
                                  return;
                                }
                                const cellsToUpdate = [];
                                const cellElemComparator = (cell, elem) => {
                                  if (cell.id !== elem.i) return false;
                                  if (cell.x !== elem.x) return false;
                                  if (cell.y !== elem.y) return false;
                                  if (cell.width !== elem.w) return false;
                                  return cell.height === elem.h;
                                };
                                for (const newElem of newLayout) {
                                  const prevElem = getCellById(newElem.i);
                                  if(!!prevElem && !isEqualWith(prevElem, newElem, cellElemComparator)) {
                                    const updatedCell = {
                                      cell_id: newElem.i,
                                      x: newElem.x,
                                      y: newElem.y,
                                      width: newElem.w,
                                      height: newElem.h
                                    };
                                    cellsToUpdate.push(updatedCell);
                                  }
                                }

                                if(cellsToUpdate.length > 0) {
                                  if (this.userLayoutAction) recordLayoutChange({
                                    previous: cellsToUpdate.map(({cell_id}) => {
                                      const c = getCellById(cell_id);
                                      return { cell_id, x: c.coordinates.x, y: c.coordinates.y, width: c.coordinates.width, height: c.coordinates.height };
                                    }),
                                    next: cellsToUpdate,
                                    screenTypeId: screenType.id,
                                    mutation: queries.cells.updateCellsCoordinates,
                                  });
                                  await apolloClient.mutate({
                                    mutation: queries.cells.updateCellsCoordinates,
                                    variables: {input: cellsToUpdate, screenTypeId: screenType.id},
                                    ignoreResults: true,
                                    refetchQueries: () => [{ query: queries.pages.getWithChildren, variables: {id: pageId, screen_type_id: screenType.id} }]
                                  });
                                }
                              }}
                  >
                    {page.cells.map((cell)=> {
                      return (
                        <Link key={cell.id} to={`/project/${projectId}/page/${pageId}/cell/${cell.id}`} onClick={() => this.setState({view: 'module'})} onContextMenu={(e) => { if (!isPowerUser) return; e.preventDefault(); this.props.history.push(`/project/${projectId}/page/${pageId}/cell/${cell.id}`); this.setState({ menu: { x: e.clientX, y: e.clientY, cellId: cell.id } }); }} className={cell.id === cellId ? 'current tsk-grid-cell-link' : 'tsk-grid-cell-link'}>
                          <div className={"tsk-cell-preview"}>
                            <CellPreview cell={cell} screenType={screenType} />
                          </div>
                          <NeedAdmin>
                            <span
                              className={'tsk-delete-icon tsk-cell'}
                              onClick={async (e) => {
                                e.preventDefault();
                                await confirmDialog(deleteDialogConfirmationContent).then(
                                  async (result) => {
                                    console.log('deleting cell');
                                    await apolloClient.mutate({
                                      mutation: queries.cells.deleteCell,
                                      variables: {input: cell.id},
                                      refetchQueries: () => [{ query: queries.pages.getWithChildren, variables: {id: pageId, screen_type_id: screenType.id} }]
                                    })
                                  }
                                );
                              }}
                            >
                              <FontAwesomeIcon icon={'trash-alt'} />
                            </span>
                          </NeedAdmin>
                        </Link>
                      )
                    })}
                  </GridLayout>
                </Fragment>
              </Background>
                  </div>
                  {/* Preview stays mounted (hidden via CSS when collapsed) so it keeps
                      refreshing on save and doesn't re-fetch on every expand. */}
                  <div className='tsk-page-editor-preview'>
                    <InlinePreviewPanel projectId={projectId} pageId={pageId} screenType={screenType} />
                  </div>
                </div>
              </div>

              {/* Right rail: module editing form for the current selection */}
              <div className='tsk-editor-props' key={`props-${this.state.history.revision}`}>
                {pageEditorView(view, {cell: getCellById(cellId), page, screenType, onDuplicate: () => this.duplicateCell(cellId), onDelete: () => this.deleteCell(cellId)})}
              </div>

              {this.state.menu && (
                <ul className="tsk-context-menu" style={{ left: this.state.menu.x, top: this.state.menu.y }} role="menu" onClick={(e) => e.stopPropagation()}>
                  <li role="menuitem" onClick={() => { this.closeMenu(); this.setState({ view: 'module' }); }}>
                    <FontAwesomeIcon icon="pen" /> {t('ui.menu.edit')}
                  </li>
                  <li role="menuitem" onClick={() => { const id = this.state.menu.cellId; this.closeMenu(); this.duplicateCell(id); }}>
                    <FontAwesomeIcon icon={['fas', 'copy']} /> {t('ui.menu.duplicate')} <kbd>⌘D</kbd>
                  </li>
                  <li role="menuitem" onClick={() => { const id = this.state.menu.cellId; this.closeMenu(); window.dispatchEvent(new CustomEvent('tsk-open-copy', { detail: { mode: 'cell', id, name: '' } })); }}>
                    <FontAwesomeIcon icon="share" /> {t('ui.menu.copy_to')}
                  </li>
                  <li role="menuitem" className="is-danger" onClick={() => { const id = this.state.menu.cellId; this.closeMenu(); this.deleteCell(id); }}>
                    <FontAwesomeIcon icon="trash-alt" /> {t('ui.menu.delete')} <kbd>⌫</kbd>
                  </li>
                </ul>
              )}
            </div>
          )
        }}
      </Query>
    );
  }
}

export default injectIntl(withRouter(PageEdition));
